/**
 * Card spend-limit helpers.
 *
 * A card can carry up to five limits (per-transaction, daily, weekly, monthly,
 * total). The cards tables and the dashboard each surface one per card, and
 * both pick it with `bindingLimit`, so they always name the same one.
 */
import { DashboardCard } from "hooks/useEmployeeDashboard";
import type {
  CardPolicyLimits,
  CardSpendingSummary,
} from "utils/cards/cardRowData";

export type LimitPeriod =
  | "transaction"
  | "daily"
  | "weekly"
  | "monthly"
  | "total";

export interface BindingLimit {
  period: LimitPeriod;
  limit: number;
  /** Spend counted against it: none for the per-transaction cap, which resets on every charge. */
  spent: number;
}

// Running limits shortest period first, then the per-transaction cap: on a tie, the first listed wins.
const LIMITS: ReadonlyArray<{
  period: LimitPeriod;
  limitKey: keyof CardPolicyLimits;
  spentKey?: keyof CardSpendingSummary;
}> = [
  { period: "daily", limitKey: "daily_limit", spentKey: "dailySpent" },
  { period: "weekly", limitKey: "weekly_limit", spentKey: "weeklySpent" },
  { period: "monthly", limitKey: "monthly_limit", spentKey: "monthlySpent" },
  { period: "total", limitKey: "total_limit", spentKey: "totalSpent" },
  { period: "transaction", limitKey: "transaction_limit" },
];

/**
 * The limit that stops the next purchase first: the one with the least left to
 * spend. A per-transaction cap always has its whole amount left, so a $100 cap
 * binds over a $500 monthly limit until $400 or more is spent that month. A $0
 * limit counts as none: nothing can set one, and linked cards store 0 when Plaid
 * reports no credit limit. Returns null for a card with no limits.
 */
export function bindingLimit(
  policy?: CardPolicyLimits | null,
  summary?: CardSpendingSummary | null,
): BindingLimit | null {
  let binding: BindingLimit | null = null;
  let bindingLeft = Infinity;
  for (const { period, limitKey, spentKey } of LIMITS) {
    const limit = policy?.[limitKey];
    if (!limit || limit <= 0) continue;
    // Spend is stored as a negative amount; normalize to a positive figure.
    const spent = spentKey ? Math.abs(summary?.[spentKey] ?? 0) : 0;
    // In cents, so float error cannot break a tie against the running limit.
    const left = Math.round(limit * 100) - Math.round(spent * 100);
    if (left < bindingLeft) {
      binding = { period, limit, spent };
      bindingLeft = left;
    }
  }
  return binding;
}

export interface ConstrainingLimit {
  spent: number;
  limit: number;
  /** Period label, e.g. "Spent today". */
  label: string;
  /** Period word for the limit caption, e.g. "daily". */
  period: string;
  /** True when this is the per-transaction cap, which has no running spend: the
   * UI shows the cap alone, without a spent/limit ratio or progress bar. */
  perTransaction?: boolean;
}

const SPENT_LABELS: Record<Exclude<LimitPeriod, "transaction">, string> = {
  daily: "Spent today",
  weekly: "Spent this week",
  monthly: "Spent this month",
  total: "Spent in total",
};

/** The dashboard's reading of `bindingLimit`. Returns null when the card has no limits of any kind. */
export function mostConstrainingLimit(
  card: DashboardCard,
): ConstrainingLimit | null {
  const binding = bindingLimit(card.activePolicy, card.spendingSummary);
  if (!binding) return null;

  if (binding.period === "transaction") {
    // No period cap to chart, so headline the month's spend for "at a glance"
    // context and surface the per-charge cap as a caption instead of a bar.
    return {
      spent: Math.abs(card.spendingSummary?.monthlySpent ?? 0),
      limit: binding.limit,
      label: "Spent this month",
      period: "transaction",
      perTransaction: true,
    };
  }

  return {
    spent: binding.spent,
    limit: binding.limit,
    label: SPENT_LABELS[binding.period],
    period: binding.period,
  };
}
